"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { Loader2, CheckCircle2, Copy } from "lucide-react";
import { Modal, Button } from "@/components/ui";
import { apiGet, apiPost } from "@/lib/api-client";
import { formatVnd } from "@/lib/doc-status";
import type { OrderKind } from "@/models/order";

interface CreatedOrder {
  id: string;
  transferCode: string;
  amount: number;
  qrUrl: string | null;
  bank: { accountNumber?: string; bankCode?: string; accountName?: string };
}

export function PurchaseButton({
  kind,
  refId,
  className,
  children,
  gradePicker = false,
}: {
  kind: OrderKind;
  refId: string;
  className?: string;
  children: React.ReactNode;
  /** Hiện ô chọn khối tặng kèm (dùng khi mua gói). */
  gradePicker?: boolean;
}) {
  const router = useRouter();
  const [order, setOrder] = React.useState<CreatedOrder | null>(null);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [paid, setPaid] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [grade, setGrade] = React.useState("1");

  async function start() {
    setError(null);
    setLoading(true);
    try {
      const payload: Record<string, unknown> = { kind, ref: refId };
      if (gradePicker) payload.grade = Number(grade);
      const created = await apiPost<CreatedOrder>("/api/orders", payload);
      setOrder(created);
      setPaid(false);
      setOpen(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Không tạo được đơn hàng.");
    } finally {
      setLoading(false);
    }
  }

  // Poll trạng thái đơn khi modal mở.
  React.useEffect(() => {
    if (!open || !order || paid) return;
    let stop = false;
    const timer = setInterval(async () => {
      try {
        const o = await apiGet<{ status: string }>(`/api/orders/${order.id}`);
        if (!stop && o.status === "paid") {
          setPaid(true);
          clearInterval(timer);
          router.refresh();
        }
      } catch {
        /* bỏ qua lỗi tạm thời khi poll */
      }
    }, 4000);
    return () => {
      stop = true;
      clearInterval(timer);
    };
  }, [open, order, paid, router]);

  return (
    <>
      {gradePicker && (
        <label className="mb-2 block text-left">
          <span className="text-caption text-muted">Chọn khối tài liệu tặng kèm</span>
          <select
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-border-strong bg-surface px-3 text-body-sm text-foreground focus-visible:outline-none focus-visible:ring focus-visible:ring-ring"
            aria-label="Khối tài liệu tặng kèm"
          >
            {[1, 2, 3, 4, 5].map((g) => (
              <option key={g} value={g}>
                Lớp {g}
              </option>
            ))}
          </select>
        </label>
      )}
      <button type="button" onClick={start} disabled={loading} className={className}>
        {loading ? <Loader2 className="size-5 animate-spin" aria-hidden="true" /> : children}
      </button>
      {error && <p className="mt-1.5 text-center text-caption text-danger">{error}</p>}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="md"
        title={paid ? "Thanh toán thành công" : "Quét mã để thanh toán"}
        description={paid ? undefined : "Chuyển khoản đúng số tiền và nội dung, hệ thống tự xác nhận."}
      >
        {paid ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <CheckCircle2 className="size-14 text-success" aria-hidden="true" />
            <p className="text-body text-foreground">Đã ghi nhận thanh toán. Quyền truy cập đã được kích hoạt.</p>
            <Button onClick={() => { setOpen(false); router.refresh(); }}>Xong</Button>
          </div>
        ) : order ? (
          <div className="space-y-4">
            {order.qrUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={order.qrUrl} alt="Mã QR chuyển khoản" className="mx-auto size-56 rounded-lg border border-border" />
            ) : (
              <p className="rounded-md bg-warning/10 p-3 text-center text-body-sm text-warning">
                Chưa cấu hình SePay — chuyển khoản thủ công theo thông tin dưới đây.
              </p>
            )}
            <dl className="space-y-1.5 rounded-lg border border-border p-3 text-body-sm">
              <Row label="Số tiền" value={formatVnd(order.amount)} strong />
              <Row label="Nội dung CK" value={order.transferCode} copy />
              {order.bank.accountNumber && <Row label="Số tài khoản" value={order.bank.accountNumber} copy />}
              {order.bank.bankCode && <Row label="Ngân hàng" value={order.bank.bankCode} />}
              {order.bank.accountName && <Row label="Chủ tài khoản" value={order.bank.accountName} />}
            </dl>
            <p className="flex items-center justify-center gap-2 text-caption text-muted">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              Đang chờ xác nhận thanh toán…
            </p>
          </div>
        ) : null}
      </Modal>
    </>
  );
}

function Row({ label, value, strong, copy }: { label: string; value: string; strong?: boolean; copy?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd className={`flex items-center gap-1.5 ${strong ? "font-bold text-foreground" : "text-foreground"}`}>
        {value}
        {copy && (
          <button
            type="button"
            onClick={() => navigator.clipboard?.writeText(value)}
            className="rounded p-0.5 text-muted hover:text-primary"
            aria-label={`Sao chép ${label}`}
          >
            <Copy className="size-3.5" aria-hidden="true" />
          </button>
        )}
      </dd>
    </div>
  );
}
